import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { BaseChartSettings } from "app/shared/model/charts/user-analytic-configurations.model";
import { MatPaginator } from "@angular/material/paginator";
import { LoggingService } from "app/shared";

export interface PeriodicElement {
  name: string;
  position: number;
  weight: number;
  symbol: string;
}

@Component({
  selector: "analytics-display-list",
  templateUrl: "./analytics-display-list.component.html",
  styleUrl: "./analytics-display-list.component.css",
  standalone: false,
})
export class AnalyticsDisplayListComponent
  implements OnChanges, OnInit, AfterViewInit
{
  private _logger: LoggingService;

  @Input() isLoading: boolean = true;
  @Input() showTitle: boolean = true;
  @Input() listData: any[] = [];
  @Input() settings: BaseChartSettings | null = null;
  @Input() title: string = "";
  @Input() showTitleFromDashboard: boolean = false;
  @Input() filter: string = "";

  pageSizeOptions: number[] = [5, 10, 25, 100];
  defaultPageSize: number = BaseChartSettings.defaults.pageSize;
  private defaultPageSizeOptions: number[] = [5, 10, 25, 100];

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    this.sort = sort;
    if (this.dataSource) {
      this.dataSource.sort = this.sort;
    }
  }
  private sort!: MatSort;

  @ViewChild(MatPaginator)
  set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    if (this.dataSource && this.settings && this.settings.paginate) {
      this.dataSource.paginator = this.paginator;
    }
  }
  private paginator!: MatPaginator;

  dataSource: MatTableDataSource<any> = new MatTableDataSource([]);
  displayedColumns: string[] = [];
  secondaryColumns: string[] = [];
  secondaryColumnsDataSets: Record<string, any[]> = {}; // Map of primary rows to their secondary data
  expandedRows = new Set<PeriodicElement>();

  constructor(private logger: LoggingService) {
    this._logger = logger;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["listData"] && changes["listData"].currentValue) ||
      (changes["settings"] && changes["settings"].currentValue) ||
      (changes["secondaryColumns"] && changes["secondaryColumns"].currentValue)
    ) {
      this.updateTable();
    }
  }

  private updateTable(): void {
    this.logger.log(
      "AnalyticsDisplayListComponent.updateTable - listData:",
      this.listData
    );

    if (this.listData.length > 0) {
      const allKeys = new Set<string>();
      this.listData.forEach((item) => {
        Object.keys(item).forEach((key) => allKeys.add(key));
      });

      // Separate displayed, secondary, and action columns
      const columnKeys = Array.from(allKeys);

      this.secondaryColumns = columnKeys.filter((key) =>
        key.startsWith("__secondary_")
      );

      const actionColumns = columnKeys.filter((key) =>
        key.startsWith("__action_")
      );

      const regularColumns = columnKeys.filter(
        (key) => !key.startsWith("__secondary_") && !key.startsWith("__action_")
      );

      if (this.secondaryColumns.length > 0) {
        this.displayedColumns = ["toggle", ...regularColumns, ...actionColumns];
      } else {
        this.displayedColumns = [...regularColumns, ...actionColumns];
      }

      this.logger.log(
        "AnalyticsDisplayListComponent.updateTable - displayedColumns:",
        this.displayedColumns
      );
      this.logger.log(
        "AnalyticsDisplayListComponent.updateTable - secondaryColumns:",
        this.secondaryColumns
      );
    }

    this.dataSource.data = this.listData; // Update the data source

    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    //this.dataSource.sort = this.sort; // Connect sorting

    if (this.settings && this.settings.pageSize) {
      this.pageSizeOptions = Array.from(
        new Set([
          ...this.defaultPageSizeOptions,
          this.settings.pageSize || BaseChartSettings.defaults.pageSize,
        ])
      ).sort((a, b) => a - b);
    }

    if (this.settings && this.settings.paginate) {
      setTimeout(() => {
        this.dataSource.paginator = this.paginator; // Connect paginator if pagination is enabled
        this.dataSource._updatePaginator(this.dataSource.data.length);
      }, 200);
    } else {
      this.dataSource.paginator = null;
    }

    this.setGlobalFilter(); // Set global filter
  }

  applyGlobalFilter(target: EventTarget): void {
    const filterValue = (target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  private setGlobalFilter(): void {
    this.dataSource.filterPredicate = (data: any, filter: string): boolean => {
      return this.displayedColumns.some((column) => {
        const value = data[column] ? data[column].toString().toLowerCase() : "";
        return value.includes(filter);
      });
    };
  }

  toggleRow(row: PeriodicElement): void {
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
    }
  }

  isRowExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  ngOnInit(): void {
    //this.dataSource = new MatTableDataSource(this.listData);

    // Only set data, do not re-create the dataSource
    this.dataSource.data = this.listData;
  }

  ngAfterViewInit(): void {
    // Defensive: re-assign in case view was recreated
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    if (this.paginator && this.settings && this.settings.paginate) {
      this.dataSource.paginator = this.paginator;
    }
  }
}
